import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Mail, RefreshCw, CircleCheck, CircleAlert, Clock, FlaskConical, Copy, Check, Inbox, ExternalLink } from "lucide-react";
import api from "../../utils/api";
import { fmtDateTime, apiError } from "./ticketConfig";
import {
    PageHeader, Button, KpiTile, ObjectStatus, SearchField, TableCard, EmptyState, MessageStrip, Dialog,
    surface, thCls, tdCls, relTime,
} from "./fiori";

// Sent emails: everything the Hasbro workspace emailed (Control Tower "Contact / Request
// update"), what Resend did with each one, and the email exactly as sent.
// Backend: hasbroEmailsController (GET /sent-emails, /sent-emails/:id, POST /sent-emails/sync).

const DELIVERED = new Set(["delivered", "opened", "clicked"]);
const PENDING = new Set(["sent", "queued", "scheduled", "delivery_delayed"]);
const toneOf = d => (!d ? "neutral" : d.event === "test" ? "neutral" : d.failed ? "negative" : DELIVERED.has(d.event) ? "positive" : PENDING.has(d.event) ? "information" : "neutral");
const bucketOf = e => (e.dryRun ? "test" : !e.delivery?.event ? "unknown" : e.delivery.failed ? "failed" : DELIVERED.has(e.delivery.event) ? "delivered" : "pending");
const FILTERS = [["all", "All"], ["delivered", "Delivered"], ["failed", "Not delivered"], ["pending", "On its way"], ["unknown", "Not checked"], ["test", "Tests"]];

export default function HasbroSentEmails() {
    const [emails, setEmails] = useState([]);
    const [sender, setSender] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/api/hasbro/sent-emails");
            setEmails(data.emails || []);
            setSender(data.sender || null);
            setError("");
        } catch (err) {
            setError(apiError(err, "Could not load sent emails."));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const sync = async () => {
        setSyncing(true);
        setNotice("");
        try {
            const { data } = await api.post("/api/hasbro/sent-emails/sync");
            setNotice(`Checked Resend: ${data.found} email${data.found === 1 ? "" : "s"} from this sender · ${data.added} added to the list · ${data.updated} status update${data.updated === 1 ? "" : "s"}.`);
            await load();
        } catch (err) {
            setError(apiError(err, "Could not check Resend."));
        } finally {
            setSyncing(false);
        }
    };

    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return emails.filter(e => (filter === "all" || bucketOf(e) === filter)
            && (!s || [e.subject, e.to?.email, e.to?.label, e.shipmentKey, e.sentBy?.name, e.sentBy?.email, ...(e.cc || [])].some(v => String(v || "").toLowerCase().includes(s))));
    }, [emails, q, filter]);
    const count = b => emails.filter(e => bucketOf(e) === b).length;
    const real = emails.length - count("test");

    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            <PageHeader
                breadcrumb={[{ label: "Hasbro" }, { label: "Customer care" }]}
                icon={Mail}
                title="Sent Emails"
                subtitle="Every email sent from the Control Tower, whether it arrived, and the email exactly as it was sent."
                actions={<Button variant="emphasized" icon={RefreshCw} onClick={sync} disabled={syncing}>{syncing ? "Checking…" : "Check Resend"}</Button>}
            >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiTile label="Sent" value={real} icon={Mail} tone="information" active={filter === "all"} onClick={() => setFilter("all")} />
                    <KpiTile label="Delivered" value={count("delivered")} total={real} icon={CircleCheck} tone="positive" active={filter === "delivered"} onClick={() => setFilter(f => (f === "delivered" ? "all" : "delivered"))} />
                    <KpiTile label="Not delivered" value={count("failed")} total={real} icon={CircleAlert} tone={count("failed") ? "negative" : "neutral"} active={filter === "failed"} onClick={() => setFilter(f => (f === "failed" ? "all" : "failed"))} hint="Bounced, suppressed or failed" />
                    <KpiTile label="On its way / not checked" value={count("pending") + count("unknown")} total={real} icon={Clock} tone="critical" active={filter === "pending"} onClick={() => setFilter(f => (f === "pending" ? "all" : "pending"))} />
                </div>
            </PageHeader>

            {sender && (
                <MessageStrip>
                    Every email is sent as <b>{sender.from.replace(/"/g, "")}</b>. Replies and a copy of every email go to <b>{sender.replyTo}</b>.
                </MessageStrip>
            )}
            {error && <MessageStrip type="error" onClose={() => setError("")}>{error}</MessageStrip>}
            {notice && <MessageStrip type="success" onClose={() => setNotice("")}>{notice}</MessageStrip>}

            <section className={`${surface} p-4 space-y-3`}>
                <SearchField value={q} onChange={setQ} placeholder="Look up an email — recipient, subject, shipment or who sent it" className="w-full" />
                <div className="flex flex-wrap items-center gap-2">
                    {FILTERS.map(([id, label]) => (
                        <button key={id} onClick={() => setFilter(id)}
                            className={`px-3 h-7 rounded-full text-xs font-semibold border transition-colors ${filter === id
                                ? "bg-[#0087C9] border-[#0087C9] text-white" : "border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-[#0087C9]"}`}>
                            {label}{id !== "all" && <span className="ml-1 opacity-70">{count(id)}</span>}
                        </button>
                    ))}
                    <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">{shown.length} of {emails.length}</span>
                </div>
            </section>

            <TableCard title="Emails" count={shown.length}>
                {loading && !emails.length ? (
                    <div className="p-5 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-9 rounded-lg bg-slate-100 dark:bg-white/[0.05] animate-pulse" />)}</div>
                ) : !emails.length ? (
                    <EmptyState icon={Inbox} title="No emails sent yet"
                        text="Emails sent from the Control Tower (Contact / Request update on an order) show here. Check Resend to pull in any that were sent before this list existed."
                        action={<Button variant="emphasized" icon={RefreshCw} onClick={sync} disabled={syncing}>Check Resend</Button>} />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr>{["Sent", "To", "Subject", "Shipment", "Sent by", "Status"].map(h => <th key={h} className={thCls}>{h}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {shown.map(e => (
                                    <tr key={e.id} onClick={() => setOpenId(e.id)} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.03]">
                                        <td className={`${tdCls} whitespace-nowrap text-xs text-slate-500 dark:text-slate-400`} title={fmtDateTime(e.at)}>{relTime(e.at)}</td>
                                        <td className={tdCls}>
                                            <span className="block font-semibold text-[#1D2D3E] dark:text-white truncate max-w-[220px]">{e.to?.label || e.to?.email || "—"}</span>
                                            <span className="block text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px]">{e.to?.email}</span>
                                        </td>
                                        <td className={`${tdCls} max-w-[340px]`}><span className="block truncate text-[#1D2D3E] dark:text-slate-200">{e.subject}</span></td>
                                        <td className={`${tdCls} font-mono text-xs text-slate-600 dark:text-slate-300`}>{e.shipmentKey || "—"}</td>
                                        <td className={`${tdCls} text-xs text-slate-600 dark:text-slate-300`}>{e.sentBy?.name || (e.source === "resend" ? "From Resend" : "—")}</td>
                                        <td className={tdCls}>
                                            <ObjectStatus tone={toneOf(e.delivery)} icon={e.dryRun ? FlaskConical : undefined}>{e.delivery?.label || "Not checked"}</ObjectStatus>
                                        </td>
                                    </tr>
                                ))}
                                {!shown.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">No emails match.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}
            </TableCard>

            {openId && <EmailDetail id={openId} onClose={() => { setOpenId(null); load(); }} />}
        </div>
    );
}

function EmailDetail({ id, onClose }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("text");
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        setError("");
        try {
            const { data } = await api.get(`/api/hasbro/sent-emails/${id}`);
            setData(data);
        } catch (err) {
            setError(apiError(err, "Could not load this email."));
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const e = data?.email, p = data?.provider;
    const text = p?.text || e?.body || "";
    const copy = () => navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
    const Row = ({ label, children }) => (
        <div className="grid grid-cols-[110px_1fr] gap-3 py-1.5 text-sm">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="text-[#1D2D3E] dark:text-slate-200 break-all">{children || "—"}</span>
        </div>
    );

    return (
        <Dialog title={e?.subject || "Email"} onClose={onClose} width="max-w-3xl"
            footer={<>
                {text && <Button icon={copied ? Check : Copy} onClick={copy}>{copied ? "Copied" : "Copy text"}</Button>}
                <Button icon={RefreshCw} onClick={load}>Refresh status</Button>
                <Button variant="emphasized" onClick={onClose}>Close</Button>
            </>}>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            {!data && !error && <div className="h-40 rounded-lg bg-slate-100 dark:bg-white/[0.05] animate-pulse" />}
            {e && (
                <>
                    <div className="flex flex-wrap items-center gap-2">
                        <ObjectStatus tone={toneOf(e.delivery)} icon={e.dryRun ? FlaskConical : undefined}>{e.delivery?.label || "Not checked"}</ObjectStatus>
                        {e.delivery?.checkedAt && <span className="text-xs text-slate-500 dark:text-slate-400">checked {relTime(e.delivery.checkedAt)}</span>}
                        {e.source === "resend" && <span className="text-xs text-slate-500 dark:text-slate-400">· added from Resend</span>}
                    </div>
                    {e.dryRun && <MessageStrip>This was a test — it was logged but never sent.</MessageStrip>}
                    {data.providerMissing && <MessageStrip type="warning">Resend has no record of this email, so its delivery status can't be checked.</MessageStrip>}
                    {p?.failed && <MessageStrip type="error">Resend reports <b>{p.label}</b> — the email didn't reach {p.to?.join(", ") || "the recipient"}{p.cc?.length ? " (or one of the people copied)" : ""}.</MessageStrip>}

                    <div className="rounded-xl border border-slate-200 dark:border-white/[0.07] px-4 py-2 divide-y divide-slate-100 dark:divide-white/[0.05]">
                        <Row label="From">{(p?.from || e.from || "").replace(/"/g, "")}</Row>
                        <Row label="To">{(p?.to?.length ? p.to : [e.to?.email]).filter(Boolean).join(", ")}{e.to?.label && e.to.label !== e.to.email ? ` (${e.to.label})` : ""}</Row>
                        <Row label="Cc">{(p?.cc?.length ? p.cc : e.cc || []).join(", ")}</Row>
                        <Row label="Reply-To">{(p?.replyTo?.length ? p.replyTo : [e.replyTo]).filter(Boolean).join(", ")}</Row>
                        <Row label="Sent">{fmtDateTime(p?.createdAt || e.at)}</Row>
                        <Row label="Sent by">{e.sentBy ? `${e.sentBy.name} (${e.sentBy.email})` : e.source === "resend" ? "Not recorded — sent before this list existed" : null}</Row>
                        <Row label="Shipment">{e.shipmentKey && <span className="font-mono">{e.shipmentKey}</span>}</Row>
                        {e.providerId && <Row label="Resend id"><span className="font-mono text-xs">{e.providerId}</span></Row>}
                    </div>

                    <div className="flex items-center gap-1 border-b border-slate-200 dark:border-white/[0.07]">
                        {[["text", "Text"], ["html", "As sent"]].map(([k, label]) => (
                            <button key={k} onClick={() => setTab(k)} disabled={k === "html" && !p?.html}
                                className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px disabled:opacity-40 ${tab === k ? "border-[#0087C9] text-[#0065A0] dark:text-[#6FD0F5]" : "border-transparent text-slate-500"}`}>
                                {label}{k === "html" && <ExternalLink size={12} className="inline ml-1 -mt-0.5" />}
                            </button>
                        ))}
                    </div>
                    {tab === "html" && p?.html ? (
                        // Shown exactly as sent, in a sandbox — no scripts, no links that navigate this page
                        <iframe title="Email as sent" sandbox="" srcDoc={p.html} className="w-full h-[420px] rounded-xl border border-slate-200 dark:border-white/[0.07] bg-white" />
                    ) : (
                        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-[#1D2D3E] dark:text-slate-200 bg-slate-50 dark:bg-white/[0.03] rounded-xl p-4 max-h-[420px] overflow-auto">{text || "No text stored for this email."}</pre>
                    )}
                </>
            )}
        </Dialog>
    );
}
